import {
  BedIcon,
  CheckmarkCircleIcon,
  EditIcon,
  HeartFilledIcon,
  HeartIcon,
  InfoIcon,
} from '@calizahq/react-components-v2'
import { FurnishedMarketAPI_Data } from '@sales-app/api/DisplayCity/DisplayCity'
import { getNMTLinkQueryParams, getPDPLinkQueryParams } from '@sales-app/context/workspace/homeFilters.util'
import { HomeFilters, WorkspaceTab } from '@sales-app/context/workspace/workspace.type'
import { PinFavHome, RecommendedHomeRow } from '@sales-app/hooks/useFavoritesPinnedHomes/useFavoritesPinnedHomes.util'
import { BaseHome } from '@sales-app/types/Home'
import { Tooltip } from '@shared/components'
import { isValid, lastDayOfMonth } from 'date-fns'
import React from 'react'

import { ReactComponent as LinkIcon } from '@/assets/icons/link.svg'
import { ReactComponent as PinIcon } from '@/assets/icons/pin.svg'
import { ReactComponent as PinFilledIcon } from '@/assets/icons/pinFilled.svg'
import { useAlertContext } from '@/context'
import { formatDate, parseDate } from '@/util'

import { ReservationType } from '../../ControlSection/Filters/ReservationTypeSelection'
import { formatMoney } from './MapView/MapPopup/MapPopup.util'

export const RecomendedHomesColumns = (
  filters: HomeFilters,
  isLoading: boolean,
  handlePinHomeClick: (home: RecommendedHomeRow) => void,
  handlefavoriteHomeClick: (home: RecommendedHomeRow) => void,
) => [
  {
    Header: 'Unit',
    width: 80,
    Cell: function Cell({ row }: { row: { original: RecommendedHomeRow } }) {
      const buttonClassName = 'grid justify-center items-center w-5 h-5 mr-2'
      const { setAlert } = useAlertContext()
      const { reservationType } = filters
      const PAGE_URL = process.env.PAGE_URL
      const ADMIN_URL = process.env.ADMIN_URL
      const home = row.original

      const getPDPLink = () => {
        const queryParams = getPDPLinkQueryParams(filters, home)

        return reservationType === ReservationType.Standby
          ? `${PAGE_URL}/homes/${row.original.slug}/standby${queryParams}`
          : `${PAGE_URL}/homes/${row.original.slug}${queryParams}`
      }

      const openPDP = () => {
        const url = getPDPLink()
        window.open(url, '_blank')
      }
      const copySharedLink = () => {
        const url = getPDPLink()
        navigator.clipboard.writeText(url)
        setAlert('Shared link copied', 'success')
      }

      const onPinHomeClick = () => {
        row.original && handlePinHomeClick(row.original)
      }

      const onFavoriteHomeClick = () => {
        row.original && handlefavoriteHomeClick(row.original)
      }

      const routeToNMTReservationTool = () => {
        const queryParams = getNMTLinkQueryParams(filters, home)
        if (!queryParams) return
        const url = `${ADMIN_URL}/admin/reservation-booker${queryParams}`
        window.open(url, '_blank')
      }

      return (
        <div className="flex w-fit-content items-center">
          <div className="h-14 w-14">
            <img
              alt="home-image"
              aria-hidden="true"
              className="h-14 w-14 cursor-pointer overflow-hidden rounded-lg object-cover"
              onClick={openPDP}
              src={row.original.homeImage}
            />
          </div>
          <div className="ml-1 flex flex-col">
            <p className=" mb-1 text-p-xs">{row.original.name}</p>
            <p className=" text-p-xs text-secondary">{row.original.street}</p>
            <div className="ml-1 flex">
              <button className={buttonClassName} onClick={copySharedLink}>
                <LinkIcon className="w-4 cursor-pointer text-green-300" />
              </button>
              <button className={buttonClassName} onClick={routeToNMTReservationTool}>
                <EditIcon className="w-4 cursor-pointer text-green-300" />
              </button>
              {row.original.potentialBookingId && (
                <button className={buttonClassName} disabled={isLoading} onClick={onPinHomeClick}>
                  {home.pinned ? (
                    <PinFilledIcon className="w-4 cursor-pointer text-green-300" />
                  ) : (
                    <PinIcon className="w-4 cursor-pointer text-green-300" />
                  )}
                </button>
              )}
              {row.original.userId && (
                <button className={buttonClassName} disabled={isLoading} onClick={onFavoriteHomeClick}>
                  {home.favorite ? (
                    <HeartFilledIcon className="w-4 cursor-pointer text-red-300" />
                  ) : (
                    <HeartIcon className="w-4 cursor-pointer text-green-300" />
                  )}
                </button>
              )}
              {home.installed && (
                <Tooltip text="Installed">
                  <BedIcon className="text-orange-700 w-4 " />
                </Tooltip>
              )}

              {home.property_approved && (
                <Tooltip text="Corporate approved">
                  <CheckmarkCircleIcon className="ml-3 w-4 " />
                </Tooltip>
              )}
            </div>
          </div>
        </div>
      )
    },
  },
  {
    Header: 'Bed/Bath',
    accessor: 'bedBath',
    width: 70,
  },
  {
    Header: 'Sq ft',
    accessor: 'square_footage',
    width: 30,
  },
  {
    Header: 'Earliest Available',
    accessor: 'earliestAvailable',
    width: 50,
  },
  {
    Header: 'Price',
    width: 60,
    Cell: function Cell({ row }: { row: { original: HomeRow } }) {
      const { reservationType, moveInStartDate } = filters

      if (reservationType === ReservationType.Standby) return ''
      if (!moveInStartDate) return 'Move In required'
      const { isSpecialOffer, displayPrice, durationText, displayPriceWithoutDiscount, priceTooltip } = row.original

      return (
        <div className="flex w-fit-content flex-wrap items-baseline">
          {isSpecialOffer ? (
            <div className="flex">
              <div className="mr-1 flex-col items-center">
                <div
                  className="flex w-full justify-center text-grey-600 line-through"
                  data-testid="special-offer-normal-price"
                >
                  {formatMoney(displayPriceWithoutDiscount, true)}
                </div>
                <div className="flex items-baseline border-b pb-0.5">
                  <span className="font-bold text-black  ">{formatMoney(displayPrice, true)}</span>
                  <span
                    className="ml-px font-bold text-label-xs text-black md:text-label-sm "
                    data-testid="home-duration"
                  >
                    {durationText}
                  </span>
                </div>
              </div>
              {priceTooltip && (
                <Tooltip autoHideTooltip={true} isToggledByClick={false} margin={38} text={priceTooltip}>
                  <InfoIcon className="w-4 cursor-pointer text-black" />
                </Tooltip>
              )}
            </div>
          ) : (
            <>
              <span className="font-bold text-black md:font-medium" data-testid="home-price">
                {formatMoney(displayPrice, true)}
              </span>
              <span className="ml-px  font-medium text-label-xs" data-testid="home-duration">
                {durationText}
              </span>
            </>
          )}
        </div>
      )
    },
  },
]

export const getStatusTagClassName = (): string => {
  return 'bg-emphasis text-black text-p-sm'
}

export interface HomeRow {
  area: number
  baths: number | null
  bedBath: string
  bedrooms: number
  bookablePersonId: number
  displayPrice: number
  displayPriceWithoutDiscount?: number
  durationText: string
  earliestAvailable: string
  homeImage: string
  id: number
  isSpecialOffer: boolean
  market: string
  name: string
  offerExpiration?: string | null
  potentialBookingId: number | null
  priceTooltip?: string
  slug: string
  street: string
}

export const transformHomes = (
  displayCityData: FurnishedMarketAPI_Data,
  potentialBookingId: number | null,
  mcHomes: PinFavHome[],
  bookablePerson: WorkspaceTab,
): RecommendedHomeRow[] => {
  const { homes = [], display_city } = displayCityData
  return homes.map((home) => {
    const pricingFields = getPricingFields(home)
    const _home = {
      ...home,
      potentialBookingId,
      ...pricingFields,
      displayCityId: display_city?.id,
      displayCityName: display_city?.name ? display_city?.name : undefined,
      bookablePersonType: bookablePerson.bookablePersonType,
      userId: bookablePerson.userId ? bookablePerson.userId : undefined,
      bookablePersonId: bookablePerson.bookablePersonId,
    }

    const index = mcHomes.findIndex((h) => h.homeId === home.id)
    if (index >= 0) {
      const mcHome = mcHomes[index]
      const { pinnedId, favoriteId, favorite, pinned } = mcHome
      return {
        ..._home,
        pinnedId,
        favoriteId,
        favorite,
        pinned,
      }
    } else {
      return {
        ..._home,
        favorite: false,
        pinned: false,
      }
    }
  })
}

export function getPricingFields(home: BaseHome) {
  const {
    bedrooms,
    baths,
    dynamic_images,
    special_offer,
    display_price_with_discount,
    display_price_without_discount,
    display_price_label,
    offer_discount_expiration,
  } = home
  const availableDate = getHomeClosestAvailableDate(home)
  const bedBath = `${bedrooms ? `${bedrooms} bd` : 'Studio'}, ${baths} ba`
  const isSpecialOffer = special_offer && display_price_with_discount !== display_price_without_discount
  const displayPrice = isSpecialOffer ? display_price_with_discount : display_price_without_discount
  const parsedAvailableDate = availableDate ? parseDate(availableDate) : null
  const expiratrion = offer_discount_expiration
    ? formatDate(parseDate(offer_discount_expiration), 'MMM d')
    : parsedAvailableDate && isValid(parsedAvailableDate)
      ? formatDate(lastDayOfMonth(parsedAvailableDate), 'MMM d')
      : null

  const earliestAvailable = availableDate ? formatDate(parseDate(availableDate), 'MMM dd, yyyy') + '' : ''
  return {
    bedBath,
    earliestAvailable,
    homeImage:
      dynamic_images && dynamic_images.length ? `${dynamic_images[0].dynamic_image_url}?width=400&height=400 ` : '',

    isSpecialOffer,
    displayPrice,
    displayPriceWithoutDiscount: display_price_without_discount,
    durationText: display_price_label || '',
    offerExpiration: expiratrion,
    priceTooltip: `This special offer rate expires ${expiratrion}`,
  }
}

interface ClosestAvailableDate {
  available_from?: string | null
  closest_available_installed_date?: string | null
  closest_available_ondemand_date?: string | null
  start_date_or_first_available_start_date?: string | null
}

export function getHomeClosestAvailableDate(home: ClosestAvailableDate | undefined) {
  return (
    home?.closest_available_installed_date ||
    home?.closest_available_ondemand_date ||
    home?.available_from ||
    home?.start_date_or_first_available_start_date
  )
}
